import React, { useState, useEffect, useCallback } from 'react';
import { db, functions } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { SPORTS, getInSeasonSports, SOCCER_SPORTS } from '../../constants/sports';
import SportGamesView from './SportGamesView';
import PlayerPropsView from './PlayerPropsView';
import BetSlip from './BetSlip';
import MyBetsView from './MyBetsView';

function SportsBettingPage({ user, userData, onUserDataChange, onBack, isMobile }) {
  const [oddsData, setOddsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('games'); // 'games' | 'mybets'
  const [selectedSport, setSelectedSport] = useState(null);
  const [betSlipLegs, setBetSlipLegs] = useState([]);
  const [betSlipOpen, setBetSlipOpen] = useState(false);
  const [bets, setBets] = useState([]);
  const [betsLoading, setBetsLoading] = useState(false);
  const [propsGame, setPropsGame] = useState(null);

  // Determine which sports are in season
  const inSeasonSports = getInSeasonSports();
  const soccerInSeason = inSeasonSports.filter(s => SOCCER_SPORTS.includes(s.key));
  const nonSoccerInSeason = inSeasonSports.filter(s => !SOCCER_SPORTS.includes(s.key));

  // Build display tabs: individual US sports + grouped "Soccer" if any soccer is in season
  const sportTabs = [
    ...nonSoccerInSeason,
    ...(soccerInSeason.length > 0 ? [{ key: '_soccer', label: 'Soccer', icon: '⚽', isSoccerGroup: true }] : []),
  ];
  const [soccerSubTab, setSoccerSubTab] = useState(soccerInSeason[0]?.key || null);

  // Auto-select first sport
  useEffect(() => {
    if (!selectedSport && sportTabs.length > 0) {
      setSelectedSport(sportTabs[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sportTabs.length]);

  // Fetch odds data (poll every 60s)
  const fetchOdds = useCallback(async () => {
    try {
      const snap = await getDoc(doc(db, 'sportsOdds', 'current'));
      if (snap.exists()) {
        setOddsData(snap.data());
      }
    } catch (err) {
      console.warn('Failed to fetch odds:', err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchOdds();
    const interval = setInterval(fetchOdds, 60000);
    return () => clearInterval(interval);
  }, [fetchOdds]);

  // Load user bets
  const loadBets = useCallback(async () => {
    if (!user) return;
    setBetsLoading(true);
    try {
      const fn = httpsCallable(functions, 'getSportsBets');
      const result = await fn({ limit: 50 });
      setBets(result.data || []);
    } catch (err) {
      console.error('Failed to load bets:', err);
    }
    setBetsLoading(false);
  }, [user]);

  useEffect(() => {
    loadBets();
  }, [loadBets]);

  // Trigger manual odds fetch if data is stale or missing, then re-poll
  useEffect(() => {
    async function triggerIfStale() {
      try {
        const snap = await getDoc(doc(db, 'sportsOdds', 'current'));
        const data = snap.exists() ? snap.data() : null;
        if (!data || Date.now() - data.lastFetched > 120000) {
          const fn = httpsCallable(functions, 'fetchSportsOddsManual');
          await fn({});
          // Re-fetch after manual trigger populates data
          await fetchOdds();
        }
      } catch (err) {
        console.warn('Manual odds fetch failed:', err.message);
      }
    }
    triggerIfStale();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add/remove leg from bet slip
  const toggleLeg = useCallback((leg) => {
    setBetSlipLegs(prev => {
      // Check if this exact selection already exists (toggle off)
      const exactMatch = leg.market === 'player_prop'
        ? prev.find(l => l.gameId === leg.gameId && l.market === 'player_prop' &&
            l.athleteId === leg.athleteId && l.propType === leg.propType && l.selection === leg.selection)
        : prev.find(l => l.gameId === leg.gameId && l.market === leg.market && l.selection === leg.selection);

      if (exactMatch) {
        // Remove the exact match (toggle off)
        return leg.market === 'player_prop'
          ? prev.filter(l => !(l.gameId === leg.gameId && l.market === 'player_prop' &&
              l.athleteId === leg.athleteId && l.propType === leg.propType && l.selection === leg.selection))
          : prev.filter(l => !(l.gameId === leg.gameId && l.market === leg.market && l.selection === leg.selection));
      }

      // No same-game parlays: remove any existing leg from this game, then add the new one
      const filtered = prev.filter(l => l.gameId !== leg.gameId);
      return [...filtered, leg];
    });
    if (!betSlipOpen) setBetSlipOpen(true);
  }, [betSlipOpen]);

  const clearBetSlip = useCallback(() => {
    setBetSlipLegs([]);
  }, []);

  const onBetPlaced = useCallback(() => {
    setBetSlipLegs([]);
    setBetSlipOpen(false);
    loadBets();
    onUserDataChange();
  }, [loadBets, onUserDataChange]);

  // Resolve which sport key to show games for
  const activeSportKey = selectedSport === '_soccer' ? soccerSubTab : selectedSport;
  const activeSportGames = oddsData?.sports?.[activeSportKey]?.games || [];

  if (!user || !userData) {
    return (
      <div className="sports-container">
        <h2>Sportsbook</h2>
        <p>Please log in to access sports betting.</p>
      </div>
    );
  }

  return (
    <div className={`sports-container ${isMobile ? 'sports-mobile' : ''}`}>
      <div className="sports-header">
        <div className="sports-title-row">
          {onBack && <button className="props-back-btn" onClick={onBack}>&larr; Casino</button>}
          <h2>Sportsbook</h2>
          <div className="sports-view-toggle">
            <button className={`sports-view-btn ${view === 'games' ? 'active' : ''}`} onClick={() => setView('games')}>Games</button>
            <button className={`sports-view-btn ${view === 'mybets' ? 'active' : ''}`} onClick={() => setView('mybets')}>
              My Bets
              {bets.filter(b => b.status === 'active').length > 0 && (
                <span className="sports-bet-count">{bets.filter(b => b.status === 'active').length}</span>
              )}
            </button>
          </div>
        </div>

        {view === 'games' && (
          <>
            <div className="sports-tabs">
              {sportTabs.map(tab => (
                <button
                  key={tab.key}
                  className={`sports-tab ${selectedSport === tab.key ? 'active' : ''}`}
                  onClick={() => setSelectedSport(tab.key)}
                >
                  <span className="sports-tab-icon">{tab.icon}</span>
                  <span className="sports-tab-label">{tab.label}</span>
                </button>
              ))}
            </div>

            {selectedSport === '_soccer' && soccerInSeason.length > 1 && (
              <div className="sports-subtabs">
                {soccerInSeason.map(s => (
                  <button
                    key={s.key}
                    className={`sports-subtab ${soccerSubTab === s.key ? 'active' : ''}`}
                    onClick={() => setSoccerSubTab(s.key)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div className="sports-body">
        {view === 'games' ? (
          <div className="sports-games-layout">
            <div className="sports-games-main">
              {propsGame ? (
                <PlayerPropsView
                  game={propsGame}
                  sportKey={activeSportKey}
                  betSlipLegs={betSlipLegs}
                  onToggleLeg={toggleLeg}
                  onBack={() => setPropsGame(null)}
                />
              ) : loading ? (
                <div className="sports-loading">Loading odds...</div>
              ) : inSeasonSports.length === 0 ? (
                <div className="sports-empty">No sports currently in season. Check back later!</div>
              ) : activeSportGames.length === 0 ? (
                <div className="sports-empty">No games available for {SPORTS.find(s => s.key === activeSportKey)?.label || 'this sport'}. Check back closer to game day!</div>
              ) : (
                <SportGamesView
                  games={activeSportGames}
                  sportKey={activeSportKey}
                  betSlipLegs={betSlipLegs}
                  onToggleLeg={toggleLeg}
                  onViewProps={(game) => setPropsGame(game)}
                />
              )}
            </div>

            {!isMobile && betSlipLegs.length > 0 && (
              <div className="sports-betslip-sidebar">
                <BetSlip
                  legs={betSlipLegs}
                  userData={userData}
                  onRemoveLeg={(idx) => setBetSlipLegs(prev => prev.filter((_, i) => i !== idx))}
                  onClear={clearBetSlip}
                  onBetPlaced={onBetPlaced}
                />
              </div>
            )}
          </div>
        ) : (
          <MyBetsView bets={bets} loading={betsLoading} onRefresh={loadBets} />
        )}
      </div>

      {/* Mobile bottom bet slip */}
      {isMobile && betSlipLegs.length > 0 && (
        <>
          {betSlipOpen && <div className="sports-betslip-overlay" onClick={() => setBetSlipOpen(false)} />}
          <div className={`sports-betslip-mobile ${betSlipOpen ? 'open' : ''}`}>
            {!betSlipOpen ? (
              <button className="sports-betslip-toggle" onClick={() => setBetSlipOpen(true)}>
                Bet Slip ({betSlipLegs.length})
              </button>
            ) : (
              <BetSlip
                legs={betSlipLegs}
                userData={userData}
                onRemoveLeg={(idx) => setBetSlipLegs(prev => prev.filter((_, i) => i !== idx))}
                onClear={clearBetSlip}
                onBetPlaced={onBetPlaced}
                onClose={() => setBetSlipOpen(false)}
                isMobile
              />
            )}
          </div>
        </>
      )}

      {oddsData?.lastFetched && (
        <div className="sports-last-updated">
          Odds updated {Math.round((Date.now() - oddsData.lastFetched) / 60000)}m ago
        </div>
      )}
    </div>
  );
}

export default SportsBettingPage;
